import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  TableRow,
  TableCell,
  Chip,
  Link as LinkMaterial,
  Typography,
  TextField
} from '@material-ui/core';

import {
  SaveAlt,
  OpenInNew as OpenInNewIcon,
  PeopleAlt,
  ReplayOutlined,
  ExpandMore,
  Schedule as ScheduleIcon,
  Check as CheckIcon
} from '@material-ui/icons';

import TablePagination from '~/components/Table/TablePagination';
import InputControl from '~/components/Input/InputControl';
import tableStyles from '~/components/Table/Table/styles';
import InputSelect from '~/components/Input/InputSelect';
import LinearProgress from '~/components/LinearProgress';
import GridContainer from '~/components/GridContainer';
import InputDate from '~/components/Input/InputDate';
import xlsxGenerator from '~/utils/xlsxGenerator';
import csvGenerator from '~/utils/csvGenerator';
import Table from '~/components/Table/Table';
import GridItem from '~/components/GridItem';
import { showPerson } from '~/config/routes';
import Request from '~/providers/Request';
import Button from '~/components/Button';
import Paper from '~/components/Paper';
import { toast } from 'react-toastify';

moment.locale('pt-br');

const IdsIntegration = () => {
  const { t } = useTranslation();
  const classesTable = tableStyles();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilterId, setStatusFilterId] = useState(null);
  const [unitFilterId, setUnitFilterId] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [inputSearch, setInputSearch] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [isFormDisabled, setIsFormDisabled] = useState(true);

  const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('DD/MM/YYYY HH:mm') : '-';
  };

  const formatCpf = (cpf) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const trAttr = (attr) => t(`reportIdsIntegration.attributes.${attr}`);
  const trExtra = (extra) => t(`reportIdsIntegration.extras.${extra}`);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const validateFilters = () => {
      const filters = {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        page: page + 1,
        per_page: rowsPerPage,
      };

      switch (statusFilterId) {
        case 1: // Sucesso
          filters.statusId = 1;
          break;
        case 2: // Tentar Novamente
          filters.statusId = 2;
          break;
        case 3: // Aguardando
          filters.statusId = 0; // Assuming 0 means 'Aguardando' in API
          break;
        default:
          filters.statusId = null;
      }

      if (unitFilterId === 1) filters.unitCode = '2324';
      if (unitFilterId === 2) filters.unitCode = '2483';

      if(inputSearch.trim()) {
        filters.userName = inputSearch.trim();
      }

      if (doctorName.trim()) {
        filters.doctorName = doctorName.trim();
      }

      return filters;
  };

  const fetchData = async () => {
    // Validação: só executa se as datas estiverem preenchidas
    if (!startDate || !endDate) {
      return;
    }

    setLoading(true);
    try {
      const filters = validateFilters();

      const response = await Request.get(
        '/admin/report/integration',
        filters,
        { normalizeJSON: false }
      );

      setData(response.data || []);
      setTotalRecords(response.meta.total || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao buscar dados');
    } finally {
      setLoading(false);
    }
  };

  function onExport(fileType) {
    const filters = validateFilters();
    generateFile(filters, fileType);
  }

  const generateFile = async (filters, type) => {
    try {
      const response = await Request.get(
        '/admin/report/integration',
        filters,
        { normalizeJSON: false }
      );

      const exportData = response.data || [];

      if (exportData.length === 0) {
        toast.error('Nenhum dado disponível para exportação');
        return;
      }

      const rows = [];

      // Título do relatório
      rows.push(['RELATÓRIO DE INTEGRAÇÃO IDS']);

      // Informações dos filtros aplicados
      let filtersStr = '';
      if (inputSearch.trim()) filtersStr += `Usuário: ${inputSearch.trim()}, `;
      if (doctorName.trim()) filtersStr += `Médico: ${doctorName.trim()}, `;
      if (statusFilterId) {
        const statusNames = { 1: 'Sucesso', 2: 'Tentar Novamente', 3: 'Aguardando', 4: 'Todos' };
        filtersStr += `Status: ${statusNames[statusFilterId] || 'Todos'}, `;
      }

      rows.push(['Filtros', filtersStr]);
      rows.push(['']);

      // Período
      rows.push([
        'Período',
        `${moment(startDate).format('DD/MM/YYYY')} a ${moment(endDate).format('DD/MM/YYYY')}`
      ]);

      // Totais por status
      const totalSucesso = exportData.filter(item => item.statusDescription === 'Sucesso').length;
      const totalTentarNovamente = exportData.filter(item => item.statusDescription === 'Tentar Novamente').length;
      const totalAguardando = exportData.filter(item => item.statusDescription === 'Aguardando').length;

      rows.push(['Total Sucesso', totalSucesso]);
      rows.push(['Total Tentar Novamente', totalTentarNovamente]);
      rows.push(['Total Aguardando', totalAguardando]);
      rows.push(['Total Geral', exportData.length]);

      rows.push(['']);

      // Cabeçalhos das colunas
      rows.push([
        trAttr('evolutionId'),
        trAttr('userName'),
        trAttr('userCpf'),
        trAttr('userCns'),
        trAttr('doctorName'),
        trAttr('integrationResponse'),
        trAttr('entry'),
        trAttr('exit'),
        'Unidade',
        trAttr('integrationStatus'),
        trAttr('created')
      ]);

      // Dados
      exportData.forEach(item => {
        rows.push([
          item.evolutionId,
          item.userName,
          formatCpf(item.userCpf),
          item.userCns,
          item.doctorName,
          item.integrationResponse,
          formatDate(item.entry),
          formatDate(item.exit),
          item.unitDescription || '-',
          item.statusDescription,
          formatDate(item.created)
        ]);
      });

      const filename = `ids_integracao_${moment().format('YYYY-MM-DD')}`;

      if (type === 'xlsx') {
        xlsxGenerator(`${filename}.xlsx`, rows);
      } else {
        csvGenerator(`${filename}.csv`, rows);
      }

      toast.success(`Arquivo ${type.toUpperCase()} gerado com sucesso!`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Erro ao exportar dados');
    }
  };

  const getStatusChip = (status, integrationId) => {
    if (status === 'Sucesso') {
      return (
        <Chip
          label={'Integrado'}
          style={{ backgroundColor: '#4caf50', color: 'white', fontSize: '0.9rem', width: '200px', height: '40px' }}
          size="medium"
          onDelete={() => {}}
          deleteIcon={<CheckIcon style={{color: 'white', }} />}
        />
      );
    } else if (status === 'Tentar Novamente') {
      return (
        <Chip
          label={'Tentar Novamente'}
          style={{ backgroundColor: '#f44336', color: 'white', fontSize: '0.9rem', width: '200px', height: '40px' }}
          size="medium"
          onClick={() => {
            reprocessIntegration(integrationId);
          }}
          onDelete={() => {}}
          deleteIcon={<ReplayOutlined style={{color: 'white', }} />}
        />
      );
    } else if (status === 'Aguardando') {
      return (
        <Chip
          label={'Aguardando'}
          style={{ backgroundColor: '#ff9800', color: 'white', fontSize: '0.9rem', width: '200px', height: '40px' }}
          size="medium"
          onDelete={() => {}}
          deleteIcon={<ScheduleIcon style={{color: 'white', }} />}
        />
     );
    }
  };

  const reprocessIntegration = async (integrationId) => {
    try {
      setLoading(true);
      if (!integrationId) {
        toast.error(`ID de integração inválido (${integrationId})`);
        return;
      }
      await Request.patch(`/admin/report/integration/${integrationId}/reprocess`);
      toast.success(`Reprocessamento (${integrationId}) iniciado com sucesso!`);
      // Recarrega os dados após o reprocessamento
      await fetchData();
    } catch (error) {
      console.error('Erro ao reprocessar integração:', error);
      toast.error('Erro ao iniciar reprocessamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const checkAndFetchData = () => {
    if (!startDate && !endDate) {
      setIsFormDisabled(true);
      toast.warning('Por favor, selecione as datas inicial e final');
      return;
    }
    if (!startDate || !endDate) {
      setIsFormDisabled(true);
      return;
    }
    if (moment(startDate).isAfter(moment(endDate))) {
      setIsFormDisabled(true);
      toast.error('Data inicial não pode ser posterior à data final');
      return;
    }
    if (moment(endDate).diff(moment(startDate), 'days') > 90) {
      setIsFormDisabled(true);
      toast.error('O intervalo entre as datas não pode ser superior a 90 dias');
      return;
    }
    setIsFormDisabled(false);
    fetchData();
  };

  useEffect(() => {
    // Só executa fetchData se as datas estiverem preenchidas
    if (startDate && endDate) {
      fetchData();
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    checkAndFetchData();
  }, [startDate, endDate]);

  // Debounce para campos de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (startDate && endDate && !isFormDisabled) {
        fetchData();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [inputSearch, doctorName, statusFilterId, unitFilterId]);

  return (
    <Paper
      Icon={PeopleAlt}
      iconColor="blue"
      title={t('dashboard.attributes.ids_integration')}
      subtitle="Relatórios dos prontuários que foram ou serão enviados a IDS"
    >
      <GridContainer style={{ marginTop: 10 }} alignItems="center">

        <GridItem xs={12} sm={4}>
          <TextField
            value={inputSearch}
            label={trExtra('search')}
            placeholder={'Digite o nome do usuário'}
            onChange={(e) => setInputSearch(e.target.value)}
            fullWidth
            disabled={isFormDisabled}
            InputProps={{
              endAdornment: (
                <ExpandMore style={{color: 'silver'}} />
              )
            }}
          />
        </GridItem>

        <GridItem xs={12} sm={4}>
          <TextField
            value={doctorName}
            label={trExtra('doctorName')}
            placeholder={'Digite o nome do médico'}
            onChange={(e) => setDoctorName(e.target.value)}
            fullWidth
            disabled={isFormDisabled}
            InputProps={{
              endAdornment: (
                <ExpandMore style={{color: 'silver'}} />
              )
            }}
          />
        </GridItem>

        <GridItem xs={12} sm={4} style={{ marginTop: '-5px' }}>
          <InputControl
            value={statusFilterId}
            name="integrationStatus"
            label={'Status'}
            inputComponent={InputSelect}
            disabled={isFormDisabled}
            onChange={(data) => {
              setStatusFilterId(data.integrationStatus);
            }}
            InputLabelProps={{
              style: {
                fontWeight: 200,
                color: isFormDisabled ? "#ccc" : "#333"
              }
            }}
            InputProps={{
               "state-name": "integrationStatus",
               "outValueType": "number",
               "placeholder": "Status",
               "options": [
                 { id: 4, name: 'Todos' },
                 { id: 1, name: 'Sucesso' },
                 { id: 2, name: 'Tentar Novamente' },
                 { id: 3, name: 'Aguardando' },
               ],
               "isClearable": false,
               "styles": isFormDisabled ? {
                 control: (provided) => ({
                   ...provided,
                   border: "none",
                   borderBottom: "0.1px dotted #ccc",
                   borderRadius: 0,
                   backgroundColor: "transparent",
                   boxShadow: "none"
                 }),
                 singleValue: (provided) => ({
                   ...provided,
                   color: "#ccc"
                 }),
                 placeholder: (provided) => ({
                   ...provided,
                   color: "#ccc"
                 }),
                 dropdownIndicator: (provided) => ({
                   ...provided,
                   color: "#ccc"
                 })
               } : {}
             }}
          />
        </GridItem>

        <GridItem xs={12} sm={4} style={{ marginTop: '-5px' }}>
          <InputControl
            value={unitFilterId}
            name="unitCode"
            label={'Unidade'}
            inputComponent={InputSelect}
            disabled={isFormDisabled}
            onChange={(data) => {
              setUnitFilterId(data.unitCode);
            }}
            InputLabelProps={{
              style: {
                fontWeight: 200,
                color: isFormDisabled ? "#ccc" : "#333"
              }
            }}
            InputProps={{
               "state-name": "unitCode",
               "outValueType": "number",
               "placeholder": "Unidade",
               "options": [
                 { id: 3, name: 'Todas' },
                 { id: 1, name: 'Geral (2324)' },
                 { id: 2, name: 'Especializada (2483)' },
               ],
               "isClearable": false,
               "styles": isFormDisabled ? {
                 control: (provided) => ({
                   ...provided,
                   border: "none",
                   borderBottom: "0.1px dotted #ccc",
                   borderRadius: 0,
                   backgroundColor: "transparent",
                   boxShadow: "none"
                 }),
                 singleValue: (provided) => ({
                   ...provided,
                   color: "#ccc"
                 }),
                 placeholder: (provided) => ({
                   ...provided,
                   color: "#ccc"
                 }),
                 dropdownIndicator: (provided) => ({
                   ...provided,
                   color: "#ccc"
                 })
               } : {}
             }}
          />
        </GridItem>

      </GridContainer>

      <GridContainer style={{ marginTop: 10 }} alignItems="center">

        <GridItem xs={12} sm={4}>
          <InputControl
            value={startDate}
            name="startDate"
            label={trExtra('startDate')}
            inputComponent={InputDate}
            InputProps={{
              type: 'date',
            }}
            onChange={setStartDate}
          />
        </GridItem>

        <GridItem xs={12} sm={4}>
          <InputControl
            value={endDate}
            name="endDate"
            label={trExtra('endDate')}
            inputComponent={InputDate}
            InputProps={{
              type: 'date',
            }}
            onChange={setEndDate}
          />
        </GridItem>

        <GridItem align="right" xs={12} sm={4}>
            <Button
              color="second"
              onClick={() => {
                onExport('csv');
              }}
              disabled={isFormDisabled}
            >
              {t('action.export_csv')} <SaveAlt />
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              color="orange"
              onClick={() => {
                onExport('xlsx');
              }}
              disabled={isFormDisabled}
            >
              {t('action.export_xlsx')} <SaveAlt />
            </Button>
          </GridItem>
      </GridContainer>

      <br />

      <Table
        loading={loading}
        emptyData={data.length === 0}
        columns={
          <>
            <TableCell className={classesTable.cell}>{trAttr('evolutionId')}</TableCell>
            <TableCell className={classesTable.cell}>{trAttr('userName')}</TableCell>
            <TableCell className={classesTable.cell}>{trAttr('userCpf')}</TableCell>
            <TableCell className={classesTable.cell}>{trAttr('doctorName')}</TableCell>
            <TableCell className={classesTable.cell}>{trAttr('integrationResponse')}</TableCell>
            <TableCell className={classesTable.cell}>{trAttr('entry')}</TableCell>
            <TableCell className={classesTable.cell}>{trAttr('exit')}</TableCell>
            <TableCell className={classesTable.cell}>Unidade</TableCell>
            <TableCell className={classesTable.cell}>{trAttr('integrationStatus')}</TableCell>
          </>
        }
      >
        {data.map((item) => (
          <TableRow key={`${item.evolutionId}-${Math.floor(Math.random() * 100)}` } hover className={classesTable.row}>
            <TableCell>
              <Chip
                label={item.userId}
                component={LinkMaterial}
                href={showPerson.reverse({
                  id: item.userId
                })}
                style={{ cursor: 'pointer' }}
                target="_blank"
                color="primary"
                variant="outlined"
                onDelete={() => {}}
                deleteIcon={<OpenInNewIcon />}
              />
            </TableCell>
            <TableCell>
              {item.userName}
            </TableCell>
            <TableCell>
              {formatCpf(item.userCpf)}
            </TableCell>
            <TableCell>
              <Chip
                label={item.doctorName || '-'}
                component={LinkMaterial}
                href={showPerson.reverse({
                  id: item.doctorId || ''
                })}
                style={{ cursor: 'pointer' }}
                target="_blank"
                color="primary"
                variant="outlined"
                onDelete={() => {}}
                deleteIcon={<OpenInNewIcon />}
              />
            </TableCell>
            <TableCell>
              {item.integrationResponse}
            </TableCell>
            <TableCell>
              {formatDate(item.entry)}
            </TableCell>
            <TableCell>
              {formatDate(item.exit)}
            </TableCell>
            <TableCell>
              {item.unitDescription || '-'}
            </TableCell>
            <TableCell>
              {getStatusChip(item.statusDescription, item.integrationId)}
            </TableCell>
          </TableRow>
        ))}
      </Table>

      <LinearProgress loading={loading} />

      <Typography
        variant="subtitle2"
        color="primary"
        align="right"
        style={{ paddingRight: 16, marginTop: 10 }}
      >
        <strong>Total: </strong>
        {totalRecords}
      </Typography>

      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default IdsIntegration;
